import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { money } from "@/components/opportunities/shared";
import { formatDate, formatTime } from "@/lib/dates";
import { SalesActionCenter } from "@/components/dashboard/SalesActionCenter";
import { SevenTouchCard } from "@/components/dashboard/SevenTouchCard";
import { DrilldownDrawer } from "@/components/dashboard/DrilldownDrawer";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  CircleNotch, ArrowClockwise, CalendarCheck, WarningCircle, ChartBar,
  UsersThree, CaretRight, WarningOctagon, Trophy,
} from "@phosphor-icons/react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip as ReTooltip,
  PieChart, Pie, Cell,
} from "recharts";

const TZ = "America/New_York";
const NAVY = "#002248";
const GREEN = "#65AB15";
const BLUE = "#2E86D6";
const GRAY = "#9CA3AF";

function initials(name = "") {
  return name.split(" ").map((x) => x[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();
}

function Avatar({ name, photo, size = "h-9 w-9", text = "text-xs" }) {
  return photo ? (
    <img src={photo} alt="" className={`${size} shrink-0 rounded-full object-cover`} />
  ) : (
    <span className={`flex ${size} shrink-0 items-center justify-center rounded-full bg-[#002248] ${text} font-bold text-white`}>
      {initials(name)}
    </span>
  );
}

// "today at 3:58 PM ET" / "yesterday at ..." / "09/08/2026 at ..." (ET).
function lastActiveRelative(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const day = (x) => x.toLocaleDateString("en-CA", { timeZone: TZ });
  const time = d.toLocaleTimeString("en-US", { timeZone: TZ, hour: "numeric", minute: "2-digit", hour12: true });
  const today = day(new Date());
  const yesterday = day(new Date(Date.now() - 86400000));
  if (day(d) === today) return `today at ${time} ET`;
  if (day(d) === yesterday) return `yesterday at ${time} ET`;
  const date = d.toLocaleDateString("en-US", { timeZone: TZ, month: "2-digit", day: "2-digit", year: "numeric" });
  return `${date} at ${time} ET`;
}

// ---------- Team Activity strip (other enabled members only — never self) ----------
function TeamActivity({ team }) {
  return (
    <div className="rounded-lg border border-[#E4E4E7] bg-white px-4 py-3 shadow-sm" data-testid="team-activity">
      <div className="flex flex-wrap items-center gap-x-8 gap-y-3">
        <span className="text-sm font-bold text-[#002248]">Team Activity</span>
        {(team || []).map((t) => {
          const online = t.presence === "active" || t.presence === "idle";
          const last = lastActiveRelative(t.last_genuine_activity);
          return (
            <span key={t.id} className="flex items-center gap-2.5" data-testid={`team-member-${t.id}`}>
              <span className="relative shrink-0">
                <Avatar name={t.name} photo={t.profile_picture} />
                <span className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full border-2 border-white"
                  style={{ backgroundColor: online ? "#22C55E" : GRAY }} />
              </span>
              <span className="min-w-0">
                <span className="block truncate text-sm font-semibold text-[#09090B]">{t.name}</span>
                <span className={`block text-xs ${online ? "font-semibold text-[#4c840f]" : "text-[#6D6D70]"}`}>
                  {t.presence === "active" ? "Online now"
                    : t.presence === "idle" ? "Online — idle"
                      : last ? `Offline — Last active ${last}` : "Offline — no activity recorded"}
                </span>
              </span>
            </span>
          );
        })}
        {(!team || team.length === 0) && (
          <span className="text-xs text-[#6D6D70]">No other enabled team members to display.</span>
        )}
      </div>
    </div>
  );
}

// ---------- Executive indicator cards ----------
function Indicator({ icon: Icon, iconCls, label, value, sub, subCls, onClick, testId }) {
  return (
    <button type="button" onClick={onClick} data-testid={testId}
      className="group flex items-center gap-3 rounded-lg border border-[#E4E4E7] bg-white px-4 py-3.5 text-left shadow-sm transition-colors hover:border-[#C8CCD2]">
      <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-md ${iconCls}`}>
        <Icon size={22} weight="bold" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-xs font-semibold text-[#52525B]">{label}</span>
        <span className="block font-heading text-2xl font-black leading-tight text-[#002248]" data-testid={`${testId}-value`}>{value}</span>
        {sub && <span className={`block truncate text-[11px] ${subCls || "text-[#6D6D70]"}`}>{sub}</span>}
      </span>
      <CaretRight size={16} className="shrink-0 text-[#C8CCD2] group-hover:text-[#65AB15]" />
    </button>
  );
}

function typeBreakdown(items) {
  const by = {};
  for (const i of items) by[i.action_type] = (by[i.action_type] || 0) + 1;
  const label = { call: "call", email: "email", meeting: "meeting", "follow-up": "follow-up" };
  return Object.entries(by)
    .map(([k, n]) => `${n} ${label[k] || k}${n === 1 ? "" : "s"}`)
    .join(" · ");
}

// ---------- Sales Target Progress ----------
function TargetRow({ name, photo, label, period, progress }) {
  const g = progress?.goal || {};
  const pct = progress ? Math.min(Math.round(progress.percent_of_goal), 100) : 0;
  return (
    <div className="border-b border-[#F0F1F3] px-4 py-3 last:border-0" data-testid={`target-row-${name}`}>
      <div className="flex items-center gap-3">
        <Avatar name={name} photo={photo} />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-[#09090B]">
            {name}
            <span className="ml-1.5 font-normal text-[#6D6D70]">· {label}</span>
          </p>
          {period && <p className="text-[11px] text-[#9CA3AF]">{period}</p>}
        </div>
        {progress ? (
          <span className="font-heading text-lg font-black text-[#4c840f]">{progress.percent_of_goal}%</span>
        ) : (
          <span className="text-lg font-black text-[#C8CCD2]">—</span>
        )}
      </div>
      {progress ? (
        <>
          <div className="mt-2 h-2 overflow-hidden rounded-full bg-[#EDEFF2]">
            <div className="h-full rounded-full bg-[#65AB15]" style={{ width: `${pct}%` }} />
          </div>
          <p className="mt-1.5 text-xs text-[#52525B]">
            {money(progress.credited_quota_revenue)} / {money(g.target_credited_revenue)}
            <span className="ml-2 text-[#9CA3AF]">{money(progress.remaining_to_goal)} remaining</span>
          </p>
        </>
      ) : (
        <p className="mt-1.5 text-xs italic text-[#9CA3AF]">Target pending confirmation</p>
      )}
    </div>
  );
}

// Exact-cent currency for the quota drill-down (money() rounds to whole dollars).
const moneyCents = (n) => (n == null ? "—"
  : n.toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2, maximumFractionDigits: 2 }));
const ORIGIN_SHORT = {
  shirley_originated: "Self-Originated (100%)",
  company_assigned: "Company-Assigned (50%)",
};

function DrilldownGoal({ row }) {
  const g = row.goal || {};
  const contrib = row.contributing_opportunities || [];
  return (
    <div className="rounded-md border border-[#E4E4E7]" data-testid={`drilldown-goal-${g.id}`}>
      <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-[#E4E4E7] bg-[#F8F9FA] px-3 py-2">
        <p className="text-sm font-bold text-[#002248]">
          {row.salesperson_name}
          <span className="ml-1.5 font-normal text-[#6D6D70]">· {g.label || "Sales goal"} · {fmtPeriod(g)}</span>
        </p>
        <p className="text-xs font-semibold text-[#52525B]">Target {moneyCents(g.target_credited_revenue)}</p>
      </div>
      {contrib.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead>
              <tr className="border-b border-[#F0F1F3] text-[10px] font-bold uppercase text-[#6D6D70]">
                {["Opportunity", "Organization", "Won", "Origin (credit)", "12-Mo Revenue", "Credited"].map((hd) => (
                  <th key={hd} className="px-3 py-2">{hd}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {contrib.map((o) => (
                <tr key={o.id} className="border-b border-[#F4F4F5] last:border-0" data-testid={`drilldown-opp-${o.id}`}>
                  <td className="max-w-[180px] truncate px-3 py-2 font-semibold text-[#09090B]">{o.name}</td>
                  <td className="max-w-[140px] truncate px-3 py-2 text-[#52525B]">{o.organization || "—"}</td>
                  <td className="whitespace-nowrap px-3 py-2 text-[#52525B]">{(o.won_date || "").slice(0, 10)}</td>
                  <td className="whitespace-nowrap px-3 py-2 text-[#52525B]">{ORIGIN_SHORT[o.origin_classification] || o.origin_classification}</td>
                  <td className="whitespace-nowrap px-3 py-2 tabular-nums text-[#52525B]">{moneyCents(o.first_12_month_revenue)}</td>
                  <td className="whitespace-nowrap px-3 py-2 font-semibold tabular-nums text-[#002248]">{moneyCents(o.credited_amount)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t border-[#E4E4E7] bg-[#F8F9FA] font-bold text-[#002248]">
                <td className="px-3 py-2" colSpan={5}>Total credited toward target</td>
                <td className="whitespace-nowrap px-3 py-2 tabular-nums" data-testid={`drilldown-total-${g.id}`}>{moneyCents(row.credited_quota_revenue)}</td>
              </tr>
              <tr className="text-[11px] text-[#52525B]">
                <td className="px-3 py-1.5" colSpan={5}>Remaining to goal</td>
                <td className="whitespace-nowrap px-3 py-1.5 tabular-nums">{moneyCents(row.remaining_to_goal)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      ) : (
        <p className="px-3 py-4 text-xs text-[#6D6D70]" data-testid={`drilldown-empty-${g.id}`}>
          No Closed-Won opportunities credit this target yet.
        </p>
      )}
    </div>
  );
}

function QuotaDrilldown({ rows, onClose }) {
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[85vh] overflow-y-auto rounded-lg sm:max-w-3xl" data-testid="quota-drilldown">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl font-bold text-[#002248]">Quota Progress — Detail</DialogTitle>
          <DialogDescription>
            Every Closed-Won opportunity credited toward the target, to the exact cent.
            Origin attribution sets the credit factor (Self-Originated 100%, Company-Assigned 50%).
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {rows.map((r) => <DrilldownGoal key={r.goal.id} row={r} />)}
          {rows.length === 0 && (
            <p className="py-4 text-center text-sm text-[#6D6D70]" data-testid="drilldown-no-goals">No active targets to explain yet.</p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function fmtPeriod(g) {
  const f = (s) => {
    const m = (s || "").match(/^(\d{4})-(\d{2})-(\d{2})/);
    return m ? `${m[2]}/${m[3]}/${m[1]}` : s;
  };
  if (!g?.goal_period_start) return null;
  return `${f(g.goal_period_start)} → ${f(g.goal_period_end)}`;
}

function TargetPanel({ isAdmin, core, analytics, user }) {
  const [drilldownOpen, setDrilldownOpen] = useState(false);
  const rows = isAdmin ? (core.goal_progress_by_salesperson || []) : (core.goal_progress ? [{
    salesperson_name: user?.name, profile_picture: user?.profile_picture, ...core.goal_progress,
  }] : []);
  const pending = isAdmin ? (analytics?.targets_pending || []) : (core.goal_progress ? [] : [{ id: user?.id, name: user?.name, profile_picture: user?.profile_picture }]);
  return (
    <div className="flex flex-col rounded-lg border border-[#E4E4E7] bg-white shadow-sm" data-testid="sales-target-progress">
      <div className="flex items-baseline justify-between px-4 pb-1 pt-4">
        <h2 className="text-base font-bold text-[#002248] md:text-lg">Sales Target Progress</h2>
        <button type="button" onClick={() => setDrilldownOpen(true)}
          className="text-xs font-bold text-[#2E86D6] hover:underline" data-testid="targets-view-details">
          View Details
        </button>
      </div>
      {drilldownOpen && <QuotaDrilldown rows={rows} onClose={() => setDrilldownOpen(false)} />}
      <div className="min-h-0 flex-1 overflow-y-auto">
        {rows.map((r) => (
          <TargetRow key={`${r.salesperson_id || user?.id}-${r.goal.id}`}
            name={r.salesperson_name} photo={r.profile_picture}
            label={r.goal.label || "Sales goal"} period={fmtPeriod(r.goal)} progress={r} />
        ))}
        {pending.map((p) => (
          <TargetRow key={p.id} name={p.name} photo={p.profile_picture} label="Target" period={null} progress={null} />
        ))}
        {rows.length === 0 && pending.length === 0 && (
          <p className="px-4 py-4 text-xs text-[#6D6D70]" data-testid="targets-empty">No active targets configured.</p>
        )}
      </div>
    </div>
  );
}

// ---------- Forecast strip ----------
function ForecastTile({ label, value, sub, testId }) {
  return (
    <div className="rounded-lg border border-[#E4E4E7] bg-white px-4 py-3.5 shadow-sm" data-testid={testId}>
      <p className="text-xs font-semibold text-[#52525B]">{label}</p>
      <p className="font-heading text-2xl font-black leading-tight text-[#002248]" data-testid={`${testId}-value`}>{value}</p>
      {sub && <p className="mt-0.5 text-[11px] text-[#6D6D70]">{sub}</p>}
    </div>
  );
}

// Open potential, weighted forecast, closed-won and quota are separate figures —
// never blended into a single misleading number.
function ForecastStrip({ forecast, quota }) {
  const f = forecast || {};
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4" data-testid="forecast-strip">
      <ForecastTile label="Open Pipeline Potential" value={money(f.open_potential)}
        sub={`${f.open_count || 0} open opportunit${(f.open_count || 0) === 1 ? "y" : "ies"} — best-case total`}
        testId="forecast-open-potential" />
      <ForecastTile label="Weighted Forecast" value={money(f.weighted_forecast)}
        sub="Open value × stage win probability" testId="forecast-weighted" />
      <ForecastTile label="Closed-Won (YTD)" value={money(f.closed_won_ytd)}
        sub={`${f.closed_won_count || 0} won this year`} testId="forecast-closed-won" />
      <ForecastTile label="Quota Target" value={quota.target > 0 ? money(quota.target) : "—"}
        sub={quota.target > 0 ? `${money(quota.credited)} credited toward target` : "No active target configured"}
        testId="forecast-quota" />
    </div>
  );
}

// ---------- Actionable open opportunities ----------
function OpenOpportunities({ rows, isAdmin }) {
  const navigate = useNavigate();
  const list = rows || [];
  return (
    <div className="rounded-lg border border-[#E4E4E7] bg-white shadow-sm" data-testid="open-opportunities-section">
      <div className="flex items-baseline justify-between px-4 pb-1 pt-4">
        <h2 className="text-base font-bold text-[#002248] md:text-lg">Open Opportunities — Work These Next</h2>
        <button type="button" onClick={() => navigate("/opportunities")}
          className="text-xs font-bold text-[#2E86D6] hover:underline" data-testid="open-opps-view-all">
          View all
        </button>
      </div>
      {list.length === 0 ? (
        <p className="px-4 py-5 text-xs text-[#6D6D70]" data-testid="open-opps-empty">No open opportunities right now.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-[#F0F1F3] text-[10px] font-bold uppercase text-[#6D6D70]">
                <th className="px-4 py-2">Opportunity</th>
                <th className="px-3 py-2">Organization</th>
                <th className="px-3 py-2">Stage</th>
                <th className="px-3 py-2 text-right">Prob %</th>
                <th className="px-3 py-2 text-right">Value</th>
                <th className="px-3 py-2 text-right">Weighted</th>
                <th className="px-3 py-2">Target Close</th>
                {isAdmin && <th className="px-3 py-2">Owner</th>}
              </tr>
            </thead>
            <tbody>
              {list.map((o) => (
                <tr key={o.id} onClick={() => navigate(`/opportunities/${o.id}`)}
                  className="cursor-pointer border-b border-[#F4F4F5] last:border-0 hover:bg-[#FAFAFA]"
                  data-testid={`open-opp-row-${o.id}`}>
                  <td className="max-w-[220px] truncate px-4 py-2 font-semibold text-[#09090B]">{o.name}</td>
                  <td className="max-w-[160px] truncate px-3 py-2 text-[#52525B]">{o.organization || "—"}</td>
                  <td className="whitespace-nowrap px-3 py-2 text-[#52525B]">{o.stage_name || "—"}</td>
                  <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums text-[#52525B]">{o.probability}%</td>
                  <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums text-[#52525B]">{money(o.value)}</td>
                  <td className="whitespace-nowrap px-3 py-2 text-right font-semibold tabular-nums text-[#002248]">{money(o.weighted)}</td>
                  <td className="whitespace-nowrap px-3 py-2 text-[#52525B]">{formatDate(o.target_close_date) || "—"}</td>
                  {isAdmin && <td className="whitespace-nowrap px-3 py-2 text-[#52525B]">{o.owner_name || "—"}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ---------- Charts ----------
function ChartCard({ title, right, children, testId }) {
  return (
    <div className="rounded-lg border border-[#E4E4E7] bg-white p-4 shadow-sm" data-testid={testId}>
      <div className="mb-3 flex items-center justify-between gap-2">
        <h3 className="text-sm font-bold text-[#002248]">{title}</h3>
        {right}
      </div>
      {children}
    </div>
  );
}

function EmptyNote({ children }) {
  return <p className="flex h-40 items-center justify-center text-center text-xs text-[#9CA3AF]">{children}</p>;
}

const TREND_SERIES = [
  ["calls", "Calls", GREEN], ["emails", "Emails", NAVY], ["meetings", "Meetings", BLUE], ["other", "Other", GRAY],
];

// Clean, real calendar labels: daily ticks for 7 days, every 5th day for 30,
// weekly for 60/90. The backend zero-fills the range in Eastern Time so every
// bar lands on its actual date and each activity is counted exactly once.
function trendTicks(data, days) {
  const dates = (data || []).map((r) => r.date);
  if (days <= 7) return dates;
  const step = days <= 30 ? 5 : 7;
  return dates.filter((_, i) => i % step === 0);
}

function ActivityTrend({ data, days, onDays }) {
  const hasData = (data || []).some((r) => r.calls + r.emails + r.meetings + r.other > 0);
  return (
    <ChartCard title={`${days}-Day Activity Trend (ET)`} testId="chart-activity-trend"
      right={
        <select value={days} onChange={(e) => onDays(Number(e.target.value))}
          className="rounded-md border border-[#E4E4E7] px-1.5 py-0.5 text-xs text-[#52525B]"
          data-testid="trend-range-select">
          {[7, 30, 60, 90].map((d) => <option key={d} value={d}>{`Last ${d} days`}</option>)}
        </select>
      }>
      <div className="mb-2 flex flex-wrap gap-3">
        {TREND_SERIES.map(([k, label, color]) => (
          <span key={k} className="inline-flex items-center gap-1 text-[11px] text-[#52525B]">
            <span className="h-2 w-2 rounded-full" style={{ backgroundColor: color }} /> {label}
          </span>
        ))}
      </div>
      {hasData ? (
        <ResponsiveContainer width="100%" height={160}>
          <BarChart data={data} margin={{ top: 4, right: 4, left: -22, bottom: 0 }}>
            <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#9CA3AF" }}
              ticks={trendTicks(data, days)} interval={0}
              tickFormatter={(d) => { const m = String(d).match(/-(\d{2})-(\d{2})$/); return m ? `${m[1]}/${m[2]}` : d; }} />
            <YAxis tick={{ fontSize: 10, fill: "#9CA3AF" }} allowDecimals={false} />
            <ReTooltip wrapperStyle={{ fontSize: 12 }}
              labelFormatter={(d) => { const m = String(d).match(/^(\d{4})-(\d{2})-(\d{2})$/); return m ? `${m[2]}/${m[3]}/${m[1]}` : d; }} />
            {TREND_SERIES.map(([k, label, color]) => (
              <Bar key={k} dataKey={k} name={label} stackId="a" fill={color} />
            ))}
          </BarChart>
        </ResponsiveContainer>
      ) : (
        <EmptyNote>No activity recorded in this period.</EmptyNote>
      )}
    </ChartCard>
  );
}

// Batch G: every stage row is clickable — the drawer shows the records in that
// stage plus the stage movements recorded in the selected dashboard period.
function PipelineMovement({ pipeline, onDrill }) {
  const rows = pipeline || [];
  const max = Math.max(...rows.map((r) => Number(r.value) || 0), 1);
  const any = rows.some((r) => r.count > 0);
  return (
    <ChartCard title="Pipeline Movement" testId="chart-pipeline">
      {any ? (
        <div className="space-y-1">
          {rows.map((s) => (
            <button key={s.stage_id} type="button"
              onClick={() => onDrill({ type: "pipeline", stageId: s.stage_id, title: `Pipeline — ${s.stage_name}` })}
              className="flex w-full items-center gap-2 rounded-sm px-1 py-0.5 text-left transition-colors hover:bg-[#F4F4F5]"
              data-testid={`pipeline-stage-${s.stage_id}`}>
              <span className="w-28 truncate text-xs text-[#52525B]" title={s.stage_name}>{s.stage_name}</span>
              <span className="h-3.5 flex-1 overflow-hidden rounded-sm bg-[#EDEFF2]">
                <span className="block h-full rounded-sm bg-[#002248]"
                  style={{ width: `${Math.max((Number(s.value) / max) * 100, s.count > 0 ? 4 : 0)}%` }} />
              </span>
              <span className="w-7 text-right font-heading text-sm font-black text-[#002248]">{s.count}</span>
              <span className="w-20 text-right text-xs tabular-nums text-[#52525B]">{money(s.value)}</span>
            </button>
          ))}
        </div>
      ) : (
        <EmptyNote>No open opportunities in the pipeline yet.</EmptyNote>
      )}
    </ChartCard>
  );
}

function Donut({ data, centerValue, centerLabel, testId }) {
  return (
    <div className="flex items-center gap-2">
      <div className="relative h-40 w-40 shrink-0" data-testid={testId}>
        <PieChart width={160} height={160}>
          <Pie data={data} dataKey="count" nameKey="label" innerRadius={48} outerRadius={70}
            paddingAngle={2} strokeWidth={0} className="cursor-pointer"
            onClick={(d) => d?.payload?.onClick && d.payload.onClick()}>
            {data.map((d, i) => <Cell key={i} fill={d.color} />)}
          </Pie>
          <ReTooltip wrapperStyle={{ fontSize: 12 }} />
        </PieChart>
        <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
          <span className="font-heading text-xl font-black text-[#002248]">{centerValue}</span>
          <span className="text-[10px] text-[#9CA3AF]">{centerLabel}</span>
        </div>
      </div>
      <div className="min-w-0 flex-1 space-y-1.5">
        {data.map((d) => {
          const total = data.reduce((s, x) => s + x.count, 0) || 1;
          const Row = d.onClick ? "button" : "p";
          return (
            <Row key={d.label} {...(d.onClick ? { type: "button", onClick: d.onClick, "data-testid": d.testId } : {})}
              className={`flex w-full items-start gap-1.5 text-left text-[11px] leading-snug text-[#52525B] ${d.onClick ? "cursor-pointer hover:underline" : ""}`}>
              <span className="mt-0.5 h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: d.color }} />
              <span className="min-w-0 break-words">{d.label}</span>
              <span className="ml-auto shrink-0 whitespace-nowrap font-semibold text-[#002248]">
                {d.count} ({Math.round((d.count / total) * 100)}%)
              </span>
            </Row>
          );
        })}
      </div>
    </div>
  );
}

const MIX_COLORS = [GREEN, NAVY, BLUE, "#8A6FD1", "#D97706", GRAY];

// Batch G: every outcome category (legend row or chart section) opens the
// drawer listing the underlying activities. The "No outcome recorded" bucket
// keeps its manual-correction path via a link inside the drawer (item 5).
function OutcomeMix({ mix, total, onDrill }) {
  const data = (mix || []).map((m, i) => ({
    ...m,
    color: m.label === "No outcome recorded" ? GRAY : MIX_COLORS[i % MIX_COLORS.length],
    onClick: () => onDrill({ type: "outcome", key: m.label, title: `Activity Outcomes — ${m.label}` }),
    testId: m.label === "No outcome recorded" ? "outcome-no-outcome-link"
      : `outcome-legend-${m.label.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`,
  }));
  return (
    <ChartCard title="Activity Outcomes" testId="chart-outcome-mix">
      {data.length > 0 ? (
        <Donut data={data} centerValue={total} centerLabel="Outcomes" testId="outcome-donut" />
      ) : (
        <EmptyNote>No outcomes recorded in this period.</EmptyNote>
      )}
    </ChartCard>
  );
}

// Admin to-do: approved wins whose Admin Hub transfer is still outstanding.
// Stays visible until the Hub confirms Complete.
const HANDOFF_BADGE = {
  pending: ["Pending", "bg-[#F59E0B]/10 text-[#B45309]"],
  needs_review: ["Needs Review", "bg-[#F59E0B]/10 text-[#B45309]"],
  failed: ["Failed", "bg-[#EF4444]/10 text-[#B91C1C]"],
};

function PendingHandoffs({ rows }) {
  const navigate = useNavigate();
  if (!rows || rows.length === 0) return null;
  return (
    <div className="rounded-md border border-[#F59E0B]/50 bg-[#F59E0B]/5 px-4 py-3" data-testid="pending-handoffs">
      <div className="flex items-center gap-2">
        <WarningCircle size={16} weight="fill" className="text-[#B45309]" />
        <p className="text-xs font-bold uppercase tracking-wide text-[#8a5a06]" data-testid="pending-handoffs-title">
          Admin Hub transfers to complete ({rows.length})
        </p>
      </div>
      {rows.map((r) => {
        const [label, cls] = HANDOFF_BADGE[r.handoff_status] || HANDOFF_BADGE.pending;
        return (
          <div key={r.id} className="mt-2 flex flex-wrap items-center justify-between gap-2 rounded-sm border border-[#F59E0B]/20 bg-white px-3 py-2" data-testid={`handoff-todo-${r.id}`}>
            <div className="min-w-0 flex-1 text-xs">
              <p className="truncate font-bold text-[#032852]">
                {r.organization || "—"} <span className="font-semibold text-[#52525B]">· {r.name}</span>
                <span className={`ml-2 rounded-sm px-1.5 py-0.5 font-bold ${cls}`}>{label}</span>
              </p>
              <p className="mt-0.5 truncate text-[#52525B]">
                Won {r.won_date ? formatDate(r.won_date) : "—"}
                {r.first_12_month_revenue ? ` · 12-month value ${money(r.first_12_month_revenue)}` : ""}
                {r.handoff_error ? ` · ${r.handoff_error}` : ""}
              </p>
            </div>
            <button type="button" onClick={() => navigate(`/opportunities/${r.id}`)}
              className="shrink-0 rounded-sm bg-[#032852] px-3 py-1.5 text-xs font-bold text-white hover:bg-[#032852]/85"
              data-testid={`handoff-todo-open-${r.id}`}>
              Open Transfer
            </button>
          </div>
        );
      })}
    </div>
  );
}

// Prominent administrator panel: pending Closed-Won review requests. Surfaced at
// the very top of the admin dashboard — each row links to the exact opportunity.
function PendingCloseReviews({ rows }) {
  const navigate = useNavigate();
  if (!rows || rows.length === 0) return null;
  return (
    <div className="rounded-md border border-[#569A1F]/50 bg-[#569A1F]/5 px-4 py-3" data-testid="pending-close-reviews">
      <div className="flex items-center gap-2">
        <Trophy size={16} weight="fill" className="text-[#457d18]" />
        <p className="text-xs font-bold uppercase tracking-wide text-[#2f5a0e]" data-testid="pending-close-reviews-title">
          Closed-Won review requested — your confirmation needed ({rows.length})
        </p>
      </div>
      {rows.map((r) => (
        <div key={r.id} className="mt-2 flex flex-wrap items-center justify-between gap-2 rounded-sm border border-[#569A1F]/20 bg-white px-3 py-2"
          data-testid={`close-review-${r.id}`}>
          <div className="min-w-0 flex-1 text-xs">
            <p className="truncate font-bold text-[#032852]">
              {r.organization || "—"} <span className="font-semibold text-[#52525B]">· {r.name}</span>
            </p>
            <p className="mt-0.5 text-[#52525B]">
              Requested by {r.requested_by_name || r.owner_name || "—"}
              {r.close_requested_at ? ` on ${formatDate(r.close_requested_at)}` : ""}
              {r.first_12_month_revenue ? ` · 12-month value ${money(r.first_12_month_revenue)}` : ""}
              {r.opportunity_type === "tag" ? " · Tag work" : ""}
            </p>
          </div>
          <button type="button" onClick={() => navigate(`/opportunities/${r.id}`)}
            className="shrink-0 rounded-sm bg-[#569A1F] px-3 py-1.5 text-xs font-bold text-white hover:bg-[#457d18]"
            data-testid={`close-review-open-${r.id}`}>
            Review Opportunity
          </button>
        </div>
      ))}
    </div>
  );
}

// Amber banner for Global Admins: one-time overdue-close bypasses (item 7).
function BypassAlerts({ alerts, onDismiss }) {
  const navigate = useNavigate();
  if (!alerts || alerts.length === 0) return null;
  return (
    <div className="rounded-md border border-[#F59E0B]/40 bg-[#FFFBEB] px-4 py-3" data-testid="bypass-alerts">
      <p className="text-xs font-bold uppercase tracking-wide text-[#B45309]">Overdue-close bypasses — review needed</p>
      {alerts.map((n) => (
        <div key={n.id} className="mt-1.5 flex flex-wrap items-start justify-between gap-2" data-testid={`bypass-alert-${n.id}`}>
          <p className="min-w-0 flex-1 text-xs text-[#78350F]">{n.message}</p>
          <span className="flex shrink-0 gap-3">
            {n.link && (
              <button type="button" onClick={() => navigate(n.link)}
                className="text-xs font-bold text-[#B45309] underline" data-testid={`bypass-review-${n.id}`}>
                Review
              </button>
            )}
            <button type="button" onClick={() => onDismiss(n.id)}
              className="text-xs font-semibold text-[#92650a] hover:underline" data-testid={`bypass-dismiss-${n.id}`}>
              Dismiss
            </button>
          </span>
        </div>
      ))}
    </div>
  );
}

// ---------- Refresh indicator ----------
function RefreshBar({ updatedAt, onRefresh, refreshing }) {
  const [, force] = useState(0);
  useEffect(() => {
    const t = setInterval(() => force((x) => x + 1), 15000);
    return () => clearInterval(t);
  }, []);
  const secs = updatedAt ? Math.round((Date.now() - updatedAt.getTime()) / 1000) : null;
  const label = secs === null ? "" : secs < 30 ? "Updated just now"
    : secs < 90 ? "Updated 1 minute ago" : `Updated ${Math.round(secs / 60)} minutes ago`;
  return (
    <span className="flex items-center gap-2 text-xs text-[#6D6D70]" data-testid="refresh-indicator">
      <span data-testid="last-updated-label">{label}</span>
      <button type="button" onClick={onRefresh} aria-label="Refresh dashboard data"
        className="inline-flex items-center gap-1 rounded-md border border-[#E4E4E7] bg-white px-2 py-1 font-semibold text-[#002248] hover:bg-[#F4F4F5]"
        data-testid="manual-refresh-btn">
        <ArrowClockwise size={13} className={refreshing ? "animate-spin" : ""} /> Refresh
      </button>
    </span>
  );
}

// ---------- Page ----------
export default function Dashboard() {
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [core, setCore] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [actions, setActions] = useState(null);
  const [team, setTeam] = useState([]);
  const [bypassAlerts, setBypassAlerts] = useState([]);
  const [error, setError] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [trendDays, setTrendDays] = useState(30);
  const [actionFilter, setActionFilter] = useState(null);
  const [drill, setDrill] = useState(null); // Batch G drill-down drawer
  const acRef = useRef(null);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const [c, a, ac, t] = await Promise.all([
        api.get(isAdmin ? "/dashboard/admin" : "/dashboard/me"),
        api.get(`/dashboard/analytics?days=${trendDays}`),
        api.get("/action-center"),
        api.get("/presence/team-activity"),
      ]);
      setCore(c.data); setAnalytics(a.data); setActions(ac.data); setTeam(t.data);
      if (isAdmin) {
        const n = await api.get("/notifications", { params: { type: "overdue_bypass" } });
        setBypassAlerts(n.data.items.filter((i) => !i.read));
      }
      setError(false); setUpdatedAt(new Date());
    } catch {
      // An API failure must never masquerade as legitimate zeros.
      setError(true);
    } finally {
      setRefreshing(false);
    }
  }, [isAdmin, trendDays]);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh every 60s, paused while the tab is hidden.
  useEffect(() => {
    const t = setInterval(() => {
      if (document.visibilityState === "visible") load();
    }, 60000);
    return () => clearInterval(t);
  }, [load]);

  // Default action-center filter: first category with records.
  useEffect(() => {
    if (actions && actionFilter === null) {
      const order = ["due_today", "overdue", "missing_outcome", "upcoming"];
      setActionFilter(order.find((k) => (actions.categories[k] || []).length > 0) || "due_today");
    }
  }, [actions, actionFilter]);

  const focusActions = (filterKey) => {
    setActionFilter(filterKey);
    acRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  if (error && !core) {
    return (
      <div className="flex flex-col items-center gap-3 py-24" data-testid="dashboard-error">
        <WarningOctagon size={32} className="text-[#DC2626]" />
        <p className="text-sm font-semibold text-[#002248]">Live dashboard data is unavailable right now.</p>
        <p className="text-xs text-[#6D6D70]">Nothing is shown rather than showing incorrect zeros.</p>
        <button type="button" onClick={load}
          className="rounded-md bg-[#002248] px-4 py-2 text-sm font-semibold text-white hover:bg-[#001731]"
          data-testid="dashboard-retry">
          Retry
        </button>
      </div>
    );
  }
  if (!core || !analytics || !actions) {
    return <div className="flex justify-center py-24"><CircleNotch size={26} className="animate-spin text-[#002248]" /></div>;
  }

  const cats = actions.categories;
  const st = analytics.seven_touch;
  const ao = analytics.active_opportunities;
  const todayLong = new Date().toLocaleDateString("en-US", {
    timeZone: TZ, weekday: "long", month: "long", day: "numeric", year: "numeric",
  });
  const goalRows = isAdmin ? (core.goal_progress_by_salesperson || []) : (core.goal_progress ? [core.goal_progress] : []);
  const quotaTarget = goalRows.reduce((s, r) => s + (r.goal?.target_credited_revenue || 0), 0);
  const quotaCredited = goalRows.reduce((s, r) => s + (r.credited_quota_revenue || 0), 0);

  return (
    <div className="space-y-4 p-4 sm:p-6" data-testid={isAdmin ? "admin-dashboard" : "salesperson-dashboard"}>
      {error && (
        <div className="flex items-center justify-between gap-3 rounded-md border border-[#F59E0B]/40 bg-[#FFFBEB] px-4 py-2 text-xs font-semibold text-[#B45309]"
          data-testid="stale-data-banner">
          <span>Live data could not be refreshed — figures below are from the last successful update.</span>
          <button type="button" onClick={load} className="underline" data-testid="stale-retry">Retry now</button>
        </div>
      )}

      <div className="flex flex-wrap items-end justify-between gap-2">
        <div>
          <h1 className="font-heading text-3xl font-black tracking-tight text-[#002248]">Dashboard</h1>
          <p className="mt-1 text-sm text-[#6D6D70]">
            {isAdmin ? "Company-wide sales activity, targets and priorities." : `Welcome back, ${user?.name?.split(" ")[0]}. Your activity, target and priorities.`}
          </p>
        </div>
        <div className="flex flex-col items-end gap-1.5">
          <span className="text-sm font-medium text-[#52525B]" data-testid="dashboard-date">{todayLong}</span>
          <RefreshBar updatedAt={updatedAt} onRefresh={load} refreshing={refreshing} />
        </div>
      </div>

      {isAdmin && <PendingCloseReviews rows={core.pending_close_list} />}
      {isAdmin && <PendingHandoffs rows={core.pending_handoff_list} />}

      <BypassAlerts alerts={bypassAlerts} onDismiss={async (id) => {
        await api.post(`/notifications/${id}/read`).catch(() => {});
        setBypassAlerts((prev) => prev.filter((n) => n.id !== id));
      }} />

      <TeamActivity team={team} />

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4" data-testid="executive-indicators">
        <Indicator icon={CalendarCheck} iconCls="bg-[#65AB15]/12 text-[#4c840f]"
          label="Due Today" value={cats.due_today.length}
          sub={cats.due_today.length > 0 ? typeBreakdown(cats.due_today) : "All clear"}
          subCls={cats.due_today.length > 0 ? "text-[#4c840f]" : undefined}
          onClick={() => focusActions("due_today")} testId="indicator-due-today" />
        <Indicator icon={WarningCircle} iconCls="bg-[#DC2626]/10 text-[#DC2626]"
          label="Overdue" value={cats.overdue.length + cats.missing_outcome.length}
          sub={cats.overdue.length + cats.missing_outcome.length > 0 ? "Requires immediate attention" : "All clear"}
          subCls={cats.overdue.length + cats.missing_outcome.length > 0 ? "text-[#DC2626]" : undefined}
          onClick={() => focusActions(cats.overdue.length > 0 ? "overdue" : "missing_outcome")} testId="indicator-overdue" />
        <Indicator icon={ChartBar} iconCls="bg-[#002248]/8 text-[#002248]"
          label="Active Opportunities" value={ao.count}
          sub={ao.count > 0 ? `${money(ao.total_value)} total value` : "None open"}
          onClick={() => navigate("/opportunities")} testId="indicator-opportunities" />
        <Indicator icon={UsersThree} iconCls="bg-[#65AB15]/12 text-[#4c840f]"
          label="7-Touch Campaigns" value={st.active}
          sub={st.campaigns > 0 || st.eligible > 0
            ? `${st.eligible} eligible · ${st.due_today} due · ${st.overdue} overdue · ${st.completed} completed`
            : "No campaigns yet"}
          subCls={st.overdue > 0 ? "text-[#DC2626]" : undefined}
          onClick={() => navigate("/prospects")} testId="indicator-seven-touch" />
      </div>

      {/* Organization lifecycle stage summary — counts reconcile to the labeled scope */}
      {analytics.lifecycle_stages && (
        <div className="rounded-sm border border-[#E4E4E7] bg-white px-4 py-3" data-testid="lifecycle-summary">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-xs font-bold uppercase tracking-[0.15em] text-[#002248]">Organization Lifecycle</h3>
            <span className="text-[10px] text-[#6D6D70]" data-testid="lifecycle-summary-scope">
              {analytics.lifecycle_stages.scope} · Total {analytics.lifecycle_stages.total}
            </span>
          </div>
          <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1.5">
            {[["cold", "Cold", "#3B82F6"], ["warm", "Warm", "#F59E0B"], ["hot", "Hot", "#EF4444"],
              ["client", "Client", "#569A1F"], ["needs_review", "Needs review", "#A1A1AA"]].map(([k, label, color]) => (
              <span key={k} className="inline-flex items-center gap-1.5 text-sm" data-testid={`lifecycle-count-${k}`}>
                <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: color }} />
                <span className="font-heading text-base font-black text-[#09090B]">{analytics.lifecycle_stages.counts[k]}</span>
                <span className="text-xs font-semibold text-[#52525B]">{label}</span>
              </span>
            ))}
          </div>
        </div>
      )}

      <ForecastStrip forecast={analytics.forecast} quota={{ target: quotaTarget, credited: quotaCredited }} />

      <div ref={acRef} className="grid grid-cols-1 items-stretch gap-4 xl:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <SalesActionCenter isAdmin={isAdmin} data={actions}
          filter={actionFilter || "due_today"} onFilterChange={setActionFilter} onChanged={load} />
        <TargetPanel isAdmin={isAdmin} core={core} analytics={analytics} user={user} />
      </div>

      <OpenOpportunities rows={analytics.open_opportunities} isAdmin={isAdmin} />

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        <ActivityTrend data={analytics.activity_trend} days={trendDays} onDays={setTrendDays} />
        <PipelineMovement pipeline={core.pipeline} onDrill={setDrill} />
        <SevenTouchCard st={st} isAdmin={isAdmin} onChanged={load} onDrill={setDrill} />
        <OutcomeMix mix={analytics.outcome_mix} total={analytics.outcome_total} onDrill={setDrill} />
      </div>

      {drill && <DrilldownDrawer drill={drill} days={trendDays} onClose={() => setDrill(null)} />}
    </div>
  );
}
